"use client";

import Link from "next/link";
import { useActionState, useState } from "react";
import { requestOtp, verifyOtp, type LoginState } from "./actions";

export default function LoginForm({ demoCode }: { demoCode: string | null }) {
  const [phoneState, submitPhone, phonePending] = useActionState<LoginState, FormData>(
    requestOtp,
    { step: "phone" },
  );
  const [otpState, submitOtp, otpPending] = useActionState<LoginState, FormData>(verifyOtp, {
    step: "otp",
  });

  const [typed, setTyped] = useState("");

  // Once a code has been requested we stay on the OTP step unless it resets us.
  const onOtpStep = phoneState.step === "otp" && otpState.step !== "phone";
  const phone = otpState.phone ?? phoneState.phone ?? "";

  if (onOtpStep) {
    return (
      <form action={submitOtp} className="space-y-5">
        <input type="hidden" name="phone" value={phone} />
        <div>
          <p className="text-sm text-muted">Code for</p>
          <p className="nums text-lg font-semibold text-text">+91 {phone}</p>
          {phoneState.hint && <p className="mt-1 text-sm text-muted">{phoneState.hint}</p>}
        </div>

        <div>
          <label htmlFor="code" className="mb-1.5 block text-sm font-medium text-text">
            Enter the code
          </label>
          <input
            id="code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            defaultValue=""
            autoFocus
            className="nums w-full rounded-lg border border-line-strong bg-surface px-4 py-3 text-center text-2xl tracking-[0.4em] outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </div>

        {otpState.error && (
          <p className="rounded-md bg-danger-tint px-3 py-2 text-sm text-danger">
            {otpState.error}
          </p>
        )}

        <button
          type="submit"
          disabled={otpPending}
          className="w-full rounded-lg bg-brand px-4 py-3 font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
        >
          {otpPending ? "Verifying…" : "Verify & continue"}
        </button>

        {demoCode && (
          <p className="rounded-md bg-brand-tint px-3 py-2 text-center text-sm text-brand-dark">
            Test build — the code <span className="nums font-bold">{demoCode}</span> also works.
          </p>
        )}
      </form>
    );
  }

  return (
    <form action={submitPhone} className="space-y-5">
      <div>
        <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-text">
          Mobile number
        </label>
        <div className="flex items-center rounded-lg border border-line-strong bg-surface focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
          <span className="nums pl-4 text-muted">+91</span>
          <input
            id="phone"
            name="phone"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={10}
            value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="98765 43210"
            className="nums w-full bg-transparent px-3 py-3 text-lg outline-none"
          />
        </div>
        <p className="mt-1 text-xs text-muted">The mobile number on your Sportsun dealer record.</p>
      </div>

      {phoneState.error && (
        <p className="rounded-md bg-danger-tint px-3 py-2 text-sm text-danger">
          {phoneState.error}
        </p>
      )}

      <button
        type="submit"
        disabled={phonePending}
        className="w-full rounded-lg bg-brand px-4 py-3 font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
      >
        {phonePending ? "Sending…" : "Send me a code"}
      </button>

      <p className="border-t border-line pt-4 text-center text-sm text-muted">
        Don&apos;t have a login?{" "}
        <Link href="/request-login" className="font-semibold text-brand hover:underline">
          Request one
        </Link>
      </p>
    </form>
  );
}
